import { Injectable } from '@angular/core';
import { SeasonService } from '../season.service';
import { SyncedStorage } from '../../app-commons/synced-storage/synced-storage';
import { delay, filter, map, shareReplay, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { RacePlan } from '../race-plan/race-plan';
import { RacePlanStage, RacePlanStagesRecord } from '../race-plan/race-plan-stage';
import * as moment from 'moment';
import { Race } from './race';
import { environment } from '../../../environments/environment';
import { DriversService } from '../../gepard/drivers/drivers.service';
import { RaceStage, RaceStagesRecord } from './race-stage/race-stage';
import { ResultsService } from '../results/results.service';
import { concat, Observable, of } from 'rxjs';
import { Result } from '../results/result/result';
import { RaceSyncProgressEvent } from '../race-sync-progress-bar/race-sync-progress-bar.component';
import { AppService } from '../../app.service';

@Injectable({
  providedIn: 'root'
})
export class RaceService {

  constructor(
    private readonly seasonService: SeasonService,
    private readonly driversService: DriversService,
    private readonly resultsService: ResultsService,
    private readonly appService: AppService
  ) {
  }

  createRace(racePlan: RacePlan): Race {
    let results$: Observable<Result[]>;
    let syncedStorage: SyncedStorage<Result>;

    const driversByStartNumber$ = this.driversService.requestDrivers$(racePlan/*, {pollingInterval: 5000}*/).pipe(
      map((drivers) => this.driversService.toDriversByStartNumberRecord(drivers)),
      shareReplay({
        refCount: true,
        bufferSize: 1
      })
    );

    if ('resultsCouchDbName' in racePlan) {
      syncedStorage = new SyncedStorage(racePlan.resultsCouchDbName, !!environment.isAdmin);
      // results$ = syncedStorage.watchType$<Result>('Result');
      results$ = syncedStorage.allDocs$;
    } else if ('resultsJsonFile' in racePlan) {
      results$ = this.resultsService.requestResults$(racePlan.resultsJsonFile);
    } else {
      results$ = of([]);
    }

    return {
      racePlan,
      raceStages: this.createRaceStagesRecord(racePlan.stages),
      driversByStartNumber$,
      results$: results$.pipe(
        tap((results) => this.appService.fixView())
      ),
      syncedStorage
    };
  }

  createRaceStage(racePlanStage: RacePlanStage): RaceStage {
    return {
      racePlanStage,
      startAfterMicrotime: +moment(racePlanStage.startAfter) * 1000,
      endAtMicrotime: +moment(racePlanStage.endAt) * 1000,
      hardLimitResultsConfig: {
        maximumLapSeconds: racePlanStage.resultsConfig.maximumLapSeconds * 1.5,
        minimumLapSeconds: racePlanStage.resultsConfig.minimumLapSeconds * 0.5,
        bestLapsNumber: racePlanStage.resultsConfig.bestLapsNumber
      }
    };
  }

  createRaceStagesRecord(racePlanStagesRecord: RacePlanStagesRecord): RaceStagesRecord {
    const raceStagesRecord: RaceStagesRecord = {};
    for (let stageKey in racePlanStagesRecord) {
      raceStagesRecord[stageKey] = this.createRaceStage(racePlanStagesRecord[stageKey]);
    }
    return raceStagesRecord;
  }

  createRace$SyncProgress$(race$: Observable<Race>) {
    return race$.pipe(
      filter((race) => !!race.syncedStorage),
      switchMap((race) => this.createRaceSyncProgress$(race)),
      shareReplay({
        refCount: true,
        bufferSize: 1
      })
    );
  }

  createRaceSyncProgress$(race: Race) {
    if (!race.syncedStorage) {
      throw new Error('Race synced storage missing.');
    }

    return race.syncedStorage.sync$.pipe(
      switchMap((sync) => concat(
        race.results$.pipe(
          withLatestFrom(sync.db.info()),
          take(1),
          map(([results, info]) => {
            const total = info.doc_count;
            const createdAt = results.length > 0 ? results[results.length - 1].createdAt : undefined;
            const processed = results.length;
            const percent = total === 0
              ? 0
              : total === processed
                ? 100
                : Math.floor(processed / total * 100);
            return {
              display: true,
              percent,
              lastLapTime: moment(createdAt),
              lapsCount: processed
            };
          })
        ),
        sync.on$('change').pipe(
          withLatestFrom(sync.db.info()),
          map(([event, info]) => {
            const total = info.doc_count + event.change.docs_read + (event.change.pending || 0);
            const processed = info.doc_count + event.change.docs_read;
            const createdAt = event.change.docs && event.change.docs.length ? event.change.docs[event.change.docs.length - 1].createdAt : undefined;
            const percent = total === 0 || total === processed ? 100 : Math.floor(processed / total * 100);
            return {
              display: true,//event.change.pending > 0,
              percent,
              lastLapTime: moment(createdAt),
              lapsCount: processed
            };
          }),
          tap((syncProgress: RaceSyncProgressEvent) => this.appService.fixView())
        )
      )),
      shareReplay({
        refCount: true,
        bufferSize: 1
      })
    );
  }
}
